import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from "firebase/analytics";
import { initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence, signOut } from 'firebase/auth';
import { getFirestore, initializeFirestore, collection, addDoc, serverTimestamp, doc, setDoc, increment } from 'firebase/firestore';
import { 
  Play, Power, Navigation, Users, 
  Beer, Edit2, Trophy, Droplet, MessageSquare 
} from 'lucide-react';

// --- CUSTOM HOOKS & UTILS ---
import useBenderTracking from './hooks/useBenderTracking';
import useBenderSession from './hooks/useBenderSession';
import useBroadcast from './hooks/useBroadcast';
import { getWeekId } from './utils/scoring';
import { geohashEncode, coarsen } from './utils/geo';
import { getDeviceCountry, getTeamCountry } from './utils/country';

// --- COMPONENTS ---
import MapEngine from './components/MapEngine';
import EmailGate from './components/EmailGate';
import RecapCard from './components/RecapCard';
import ToastNotification from './components/ToastNotification';
import Leaderboard from './components/Leaderboard';
import DrinkSelector from './components/DrinkSelector';
import SquadManager from './components/SquadManager';
import SquadChat from './components/SquadChat';
import Dashboard from './components/Dashboard';
import OverlayCreator from './components/OverlayCreator';
import AdminDashboard from './components/AdminDashboard';
import RepBroadcastPanel from './components/RepBroadcastPanel';
import LocationPrimer from './components/LocationPrimer';
import RapSheet from './components/RapSheet';
import LogWorkoutModal from './components/LogWorkoutModal';
import useMorningWorkout from './hooks/useMorningWorkout';

// --- ACTIVE THEME (branding, labels, colors, map) ---
import theme from './themes';

// --- CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyC4j1nUhwwfSU4NZ5Ujckx8dcW6ZbzYoVo",
  authDomain: "bendermap-69.firebaseapp.com",
  projectId: "bendermap-69",
  storageBucket: "bendermap-69.firebasestorage.app",
  messagingSenderId: "458221530510",
  appId: "1:458221530510:web:63ae44b2de06e7aa7048df",
  measurementId: "G-J2NP2PR68P"
};

const app = initializeApp(firebaseConfig);

// Firestore's default WebChannel streaming transport fails inside a Capacitor/WKWebView
// (RPC 'Write'/'Listen' streams error with repeated 400s), which silently breaks live
// listeners and server sync. Forcing long-polling fixes it. Scope it to native so the
// plain web build keeps the more efficient WebChannel transport.
const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
const db = isNativeApp
  ? initializeFirestore(app, { experimentalForceLongPolling: true })
  : getFirestore(app);
// NOTE: use initializeAuth (not getAuth) with explicit persistence. getAuth() runs
// an environment auto-detection that mis-fires inside a Capacitor/WKWebView and waits
// forever on a Cordova `deviceready` event, hanging sign-in. Setting persistence
// explicitly skips that path. The list is tried in order; inMemory is the always-works
// fallback. Works identically on the plain web build.
const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence],
});
if (typeof window !== 'undefined') getAnalytics(app);

const APP_ID = 'bendermap-pro-v1';

const App = () => {
  const [view, setView] = useState('active'); 
  const [notification, setNotification] = useState(null);
  const [recenterTrigger, setRecenterTrigger] = useState(0); 
  const [isStandalone, setIsStandalone] = useState(false); 
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isRep, setIsRep] = useState(() => localStorage.getItem('bender_role') === 'rep');
  
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showDrinkSelector, setShowDrinkSelector] = useState(false);
  const [showSquadManager, setShowSquadManager] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showOverlayCreator, setShowOverlayCreator] = useState(false);
  const [showLocationPrimer, setShowLocationPrimer] = useState(false);
  const [showRapSheet, setShowRapSheet] = useState(false);
  const [showLogWorkout, setShowLogWorkout] = useState(false);

  const handleNotify = (type, message) => setNotification({ type, message });

  // Hold the native splash until React has actually painted a frame. The
  // default 500ms auto-hide (launchAutoHide is now false in capacitor.config)
  // dumped users onto a black page for the whole JS boot — several seconds of
  // "dead app" on every cold start.
  useEffect(() => {
    if (!(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.())) return;
    requestAnimationFrame(() => {
      import('@capacitor/splash-screen')
        .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 250 }))
        .catch(() => { /* plugin absent (web) — nothing to hide */ });
    });
  }, []);

  // Hybrid-athlete: morning-after workout detection + the paired Rap Sheet.
  const morning = useMorningWorkout();

  // On native, show the "Always" location primer once before the first session
  // so users understand the two-step iOS permission flow. Web starts directly.
  const handleStart = () => {
    const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
    const seenPrimer = localStorage.getItem('bq_seen_location_primer') === '1';
    if (isNativeApp && !seenPrimer) {
      setShowLocationPrimer(true);
      return;
    }
    startNight();
  };

  const confirmLocationPrimer = () => {
    localStorage.setItem('bq_seen_location_primer', '1');
    setShowLocationPrimer(false);
    startNight();
  };
  
  const {
    stats, setStats, startNight, endNight, isTracking, setIsTracking
  } = useBenderTracking(localStorage.getItem('bender_email'), handleNotify);

  const {
    user, email, setEmail, username, setUsername,
    squadId, squadMembers, hasUnreadMessages, setHasUnreadMessages
  } = useBenderSession(db, auth, APP_ID, stats, setStats, showChat, handleNotify);

  const {
    broadcasters, isBroadcasting, setIsBroadcasting, broadcastName, setBroadcastName, stopBroadcast
  } = useBroadcast(db, user, APP_ID, stats.location, isRep);

  // Log out / switch account. Without this the login gate never reappears (it only
  // renders when there's no bender_email), so staff who signed in as a regular user
  // could never reach the BENDER_ADMIN login without deleting the app.
  const handleLogout = async () => {
    if (!window.confirm('Log out? This ends the current session on this device.')) return;
    // Go off-air first — the write needs the still-signed-in user.
    try { await stopBroadcast(); } catch (e) { /* best effort */ }
    try { await signOut(auth); } catch (e) { /* best effort */ }
    [
      'bender_email', 'bender_username', 'bender_role',
      'bender_broadcasting', 'bender_broadcast_name',
      'bender_path', 'bender_stats',
    ].forEach(k => localStorage.removeItem(k));
    // Full reload rather than unwinding every hook by hand: guarantees clean state
    // and lets useBenderSession sign back in anonymously for the next user.
    window.location.reload();
  };

  // Anonymous, coarse session record written once when a bender ends. No uid,
  // no name, no precise coords — just a ~5km geohash + rounded point + totals.
  // This is the privacy-safe aggregate that feeds the admin world map + Nations.
  const sessionFinalizedRef = useRef(false);
  useEffect(() => { if (isTracking) sessionFinalizedRef.current = false; }, [isTracking]);
  const finalizeSession = async () => {
    if (sessionFinalizedRef.current) return;
    sessionFinalizedRef.current = true;
    try {
      const s = stats;
      const hasContent = (s.drinks || 0) > 0 || (s.distance || 0) > 0;
      if (!s.startTime || !hasContent) return;

      // On-device only: remember last night so the morning-after workout can
      // be paired with it (Rap Sheet). Never uploaded.
      try {
        localStorage.setItem('bender_last_session', JSON.stringify({
          startTime: s.startTime,
          endTime: Date.now(),
          drinks: s.drinks || 0,
          waters: s.waters || 0,
          distance: Math.round(s.distance || 0),
          steps: s.steps || 0,
          hydration: s.hydration ?? 0,
          drinkOfChoice: s.drinkOfChoice || null,
        }));
        localStorage.removeItem('bender_rap_sheet'); // new night, new sheet
      } catch (e) { /* ignore */ }

      const rec = {
        drinks: s.drinks || 0,
        waters: s.waters || 0,
        distance: Math.round(s.distance || 0),
        steps: s.steps || 0,
        durationMs: Date.now() - s.startTime,
        weekId: getWeekId(),
        createdAt: serverTimestamp(),
      };
      const loc = Array.isArray(s.location) ? s.location : null;
      if (loc) {
        rec.geohash = geohashEncode(loc[0], loc[1], 5);
        rec.lat = coarsen(loc[0]);
        rec.lng = coarsen(loc[1]);
      }
      // Where the night HAPPENED (world map) = device country; who it COUNTS
      // FOR (Nations battle) = the user's chosen team (defaults to device).
      const deviceCc = getDeviceCountry();
      if (deviceCc) rec.countryCode = deviceCc;
      await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'sessions'), rec);

      // Nations standings: bump this country's public totals (all-time + this
      // week). Rule-guarded: exactly +1 session, bounded drink/distance deltas.
      const cc = getTeamCountry();
      if (cc) {
        const bump = {
          countryCode: cc,
          sessions: increment(1),
          drinks: increment(rec.drinks),
          distance: increment(rec.distance),
          updatedAt: serverTimestamp(),
        };
        const statsCol = collection(db, 'artifacts', APP_ID, 'public', 'data', 'countryStats');
        await Promise.all([
          setDoc(doc(statsCol, `all_${cc}`), { ...bump, period: 'all' }, { merge: true }),
          setDoc(doc(statsCol, `${rec.weekId}_${cc}`), { ...bump, period: rec.weekId }, { merge: true }),
        ]);
        // Local "your contribution" counter for the Nations tab.
        try {
          const n = Number(localStorage.getItem('bender_my_sessions') || 0) + 1;
          localStorage.setItem('bender_my_sessions', String(n));
        } catch { /* ignore */ }
      }
    } catch (e) { console.warn('Session finalize failed', e); }
  };

  // Seed a rep's broadcast display name from their handle exactly ONCE, so
  // clearing the field to retype it doesn't snap back to the handle.
  const nameSeededRef = useRef(false);
  useEffect(() => {
    if (nameSeededRef.current) return;
    if (isRep && !broadcastName && username) {
      setBroadcastName(username);
      nameSeededRef.current = true;
    }
  }, [isRep, broadcastName, username, setBroadcastName]);

  useEffect(() => {
    const inStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || window.Capacitor?.isNativePlatform?.();
    setIsStandalone(inStandalone);
  }, []);

  const activeSquadCount = squadMembers.filter(m => m.location).length;
  const [hh, mm] = (stats.duration || "00:00:00").split(':');

  if (!email) {
    return <EmailGate db={db} auth={auth} onComplete={(e, u, isAdmin, isRepFlag) => { setEmail(e); setUsername(u); if (isAdmin) setIsAdminMode(true); if (isRepFlag) { setIsRep(true); localStorage.setItem('bender_role', 'rep'); } }} />;
  }

  if (isAdminMode) {
    return <AdminDashboard db={db} appId={APP_ID} onExit={() => setIsAdminMode(false)} />;
  }
  
  if (view === 'summary') {
    return <RecapCard stats={stats} path={stats.path} onContinue={() => setView('active')} onRestart={() => { endNight(); setView('active'); }} />;
  }

  return (
    <div className="h-[100dvh] w-screen flex flex-col relative bg-black overflow-hidden font-sans select-none">
      
      {notification && (
        <ToastNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <MapEngine
        userLocation={stats.location} path={stats.path} squadMembers={squadMembers}
        broadcasters={broadcasters.filter(b => b.id !== user?.uid)}
        stats={stats} recenterTrigger={recenterTrigger} db={db} appId={APP_ID}
      />

      {isRep && (
        <RepBroadcastPanel
          isBroadcasting={isBroadcasting} setIsBroadcasting={setIsBroadcasting}
          broadcastName={broadcastName} setBroadcastName={setBroadcastName}
          hasLocation={!!stats.location}
        />
      )}

      {!isTracking && (
        <Dashboard 
            user={user} stats={stats} squadMembers={squadMembers} squadId={squadId}
            onOpenSquad={() => setShowSquadManager(true)} onOpenOverlay={() => setShowOverlayCreator(true)} onStart={handleStart}
            onLogout={handleLogout}
            redemption={{
              status: morning.status,
              workout: morning.workout,
              lastSession: morning.lastSession,
              rapSheet: morning.rapSheet,
              onPair: () => { if (morning.pair()) setShowRapSheet(true); },
              onPairNothing: () => { if (morning.pairNothing()) setShowRapSheet(true); },
              onLogWorkout: () => setShowLogWorkout(true),
              onDismiss: morning.dismiss,
              onOpenRapSheet: () => setShowRapSheet(true),
              recheck: morning.recheck,
            }}
        />
      )}

      {/* --- EXACT REPLICA HUD --- */}
      {isTracking && (
        <>
          {/* TOP HUD ROW - RESPONSIVE SIZING FOR ALL IPHONES */}
          <div className={`absolute top-0 left-0 right-0 z-[1000] flex justify-between items-start pointer-events-none px-2 min-[400px]:px-3 pb-4 transition-all duration-300 ${isStandalone ? 'pt-14' : 'pt-4'}`}>
              
              {/* LEFT HEADER: Title & Sponsor */}
              <div className="flex flex-col items-start gap-1.5 pointer-events-auto">
                  <div className="transform -skew-x-12 bg-black/95 pr-3 pl-2 min-[400px]:pr-4 min-[400px]:pl-3 py-1.5 min-[400px]:py-2 flex items-center shadow-[4px_4px_0px_#000]">
                      <div className="flex gap-1 mr-2 min-[400px]:mr-3 transform skew-x-12">
                          <div className="w-1 min-[400px]:w-1.5 h-7 min-[400px]:h-9 bg-brand-teal"></div>
                          <div className="w-1 min-[400px]:w-1.5 h-7 min-[400px]:h-9 bg-brand-pink"></div>
                          <div className="w-1 min-[400px]:w-1.5 h-7 min-[400px]:h-9 bg-brand-pink"></div>
                      </div>
                      <div className="transform skew-x-12 flex flex-col justify-center">
                          <h1 className="text-[15px] min-[400px]:text-lg font-black italic text-brand-pink tracking-tighter uppercase drop-shadow-md leading-none whitespace-nowrap">{theme.branding.title}</h1>
                          <h2 className="text-[8px] min-[400px]:text-[10px] font-black italic text-brand-teal tracking-widest uppercase mt-1 drop-shadow-md leading-none whitespace-nowrap">{theme.labels.squadLabel}: {theme.labels.squadCountFmt.replace('{n}', squadId ? activeSquadCount : 0)}</h2>
                      </div>
                  </div>

                  <div onClick={() => setShowSquadManager(true)} className="ml-3 min-[400px]:ml-4 cursor-pointer">
                      <div className="flex flex-col items-center">
                          <span className="text-[5px] min-[400px]:text-[6px] font-black text-white uppercase tracking-widest leading-none mb-1 whitespace-nowrap drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{theme.branding.tagline}</span>
                          <img src={theme.branding.logo} alt={theme.branding.logoAlt} className="w-16 min-[400px]:w-20 h-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                      </div>
                  </div>
              </div>

              {/* RIGHT HEADER: Gauges */}
              <div className="flex flex-col items-end gap-1.5 pointer-events-auto">
                  
                  {/* DURATION GAUGE */}
                  <div className="transform -skew-x-12 bg-black/95 border-b-[4px] border-brand-teal px-2.5 min-[400px]:px-4 py-1.5 min-[400px]:py-2 shadow-[4px_4px_0px_#000] flex items-center min-w-[100px] min-[400px]:min-w-[130px]">
                      <div className="transform skew-x-12 flex items-center gap-1.5 min-[400px]:gap-2.5 w-full">
                          <svg className="w-4 h-4 min-[400px]:w-5 min-[400px]:h-5 text-brand-pink shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 2v2m-3-2h6" />
                          </svg>
                          <div className="flex flex-col items-start justify-center mt-0.5">
                              <span className="text-white font-black italic text-[9px] min-[400px]:text-[11px] tracking-widest uppercase leading-none mb-1">{theme.labels.durationLabel}</span>
                              <span className="text-brand-teal font-black italic text-xs min-[400px]:text-sm tracking-widest uppercase leading-none whitespace-nowrap">{hh}H {mm}M</span>
                          </div>
                      </div>
                  </div>
                  
                  {/* HYDRATION GAUGE */}
                  <div className="transform -skew-x-12 bg-black/95 border-b-[4px] border-brand-teal px-2.5 min-[400px]:px-4 py-1.5 min-[400px]:py-2 shadow-[4px_4px_0px_#000] flex items-center min-w-[100px] min-[400px]:min-w-[130px]">
                      <div className="transform skew-x-12 flex items-center gap-1.5 min-[400px]:gap-2.5 w-full">
                          {/* Removed the hardcoded size={} prop so Tailwind can scale it */}
                          <Droplet className="w-4 h-4 min-[400px]:w-5 min-[400px]:h-5 fill-blue-500 text-blue-500 shrink-0" />
                          <div className="flex flex-col items-start justify-center mt-0.5">
                              <span className="text-white font-black italic text-[9px] min-[400px]:text-[11px] tracking-widest uppercase leading-none mb-1">HYDRATION</span>
                              <span className="text-brand-teal font-black italic text-xs min-[400px]:text-sm tracking-widest uppercase leading-none whitespace-nowrap">{Math.round(stats.hydration)}%</span>
                          </div>
                      </div>
                  </div>

                  {/* Secondary Tools (Rank / Squad / Chat) — wrap so the Chat
                      button (added once in a squad) drops to a second line on
                      narrow phones instead of overflowing off the right edge. */}
                  <div className="flex flex-wrap justify-end gap-1.5 min-[400px]:gap-2 mt-1 mr-1">
                      <button onClick={() => setShowLeaderboard(true)} className="transform -skew-x-12 bg-zinc-900 border-2 border-zinc-700 px-2 min-[400px]:px-2.5 py-1 shadow-md hover:bg-zinc-800">
                          <div className="transform skew-x-12 flex items-center gap-1">
                              <Trophy size={10} className="text-brand-teal"/>
                              <span className="text-[7px] min-[400px]:text-[8px] font-black text-white uppercase tracking-widest">{theme.labels.rankingsLabel}</span>
                          </div>
                      </button>

                      <button onClick={() => setShowSquadManager(true)} className="transform -skew-x-12 bg-zinc-900 border-2 border-zinc-700 px-2 min-[400px]:px-2.5 py-1 shadow-md hover:bg-zinc-800">
                          <div className="transform skew-x-12 flex items-center gap-1">
                              <Users size={10} className="text-brand-pink"/>
                              <span className="text-[7px] min-[400px]:text-[8px] font-black text-white uppercase tracking-widest">{theme.labels.squadLabel}</span>
                          </div>
                      </button>

                      {squadId && (
                           <button onClick={() => { setShowChat(true); setHasUnreadMessages(false); }} className="transform -skew-x-12 bg-zinc-900 border-2 border-zinc-700 px-2 min-[400px]:px-2.5 py-1 shadow-md hover:bg-zinc-800 relative">
                              <div className="transform skew-x-12 flex items-center gap-1">
                                  <MessageSquare size={10} className="text-white"/>
                                  <span className="text-[7px] min-[400px]:text-[8px] font-black text-white uppercase tracking-widest">Chat</span>
                              </div>
                              {hasUnreadMessages && <div className="absolute -top-1 -right-1 w-2 min-[400px]:w-2.5 h-2 min-[400px]:h-2.5 bg-brand-pink border border-black rounded-full animate-pulse"></div>}
                          </button>
                      )}
                  </div>
              </div>
          </div>

          {/* BOTTOM CONTROLS */}
          <div className="absolute bottom-4 left-4 right-4 z-[1000] pointer-events-none flex flex-col gap-3 pb-safe">
              
              {/* Utility Floating Bar */}
              <div className="flex justify-between items-end pointer-events-auto mb-1">
                  <button onClick={() => setRecenterTrigger(prev => prev + 1)} className="w-12 h-12 bg-black border-2 border-zinc-600 shadow-[4px_4px_0px_var(--accent-2)] flex items-center justify-center active:scale-90 transition-transform transform -skew-x-12 ml-2">
                      <div className="transform skew-x-12"><Navigation size={20} className="text-white" /></div>
                  </button>
                  
                  <div className="flex flex-col items-end gap-2">
                      {/* LIVE STATS TRACKER ON MAP */}
                      <div className="flex gap-2">
                          <div className="transform -skew-x-12 bg-black/90 px-3 py-1 border border-zinc-700 shadow-md flex items-center">
                              <div className="transform skew-x-12 flex items-baseline gap-1.5">
                                  <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">{theme.labels.stepsLabel}</span>
                                  <span className="text-sm font-black text-white italic">{stats.steps}</span>
                              </div>
                          </div>
                          <div className="transform -skew-x-12 bg-black/90 px-3 py-1 border border-zinc-700 shadow-md flex items-center">
                              <div className="transform skew-x-12 flex items-baseline gap-1.5">
                                  <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest">{theme.labels.drinksLabel}</span>
                                  <span className="text-sm font-black text-brand-pink italic">{stats.drinks}</span>
                              </div>
                          </div>
                      </div>

                      <button onClick={() => setShowDrinkSelector(true)} className="transform -skew-x-12 bg-black px-4 py-1.5 border-2 border-zinc-700 shadow-md flex items-center gap-2 hover:border-brand-teal">
                          <div className="transform skew-x-12 flex items-center gap-2">
                              <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">{theme.labels.drinksLabel}:</span>
                              <span className="text-[10px] font-black text-brand-teal uppercase tracking-widest truncate max-w-[80px]">{stats.drinkOfChoice}</span>
                              <Edit2 size={10} className="text-white" />
                          </div>
                      </button>
                  </div>
              </div>

              {/* MASSIVE ACTION BUTTONS */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pointer-events-auto">
                  
                  {/* LEFT: REFUEL */}
                  <button
                      onClick={() => {
                          const drinks = (stats.drinks || 0) + 1;
                          setStats(s => ({ ...s, drinks: s.drinks + 1 }));
                          // Pacing nudge (guideline 1.4.3): from the 5th drink on,
                          // logging one actively pushes water instead of cheering.
                          if (drinks >= 5) {
                              setNotification({ type: 'water', message: `That's ${drinks} tonight — grab a water 💧` });
                          }
                      }}
                      className="w-full sm:flex-1 bg-brand-pink border-[3px] border-black transform -skew-x-12 shadow-[6px_6px_0px_#000] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all flex items-center justify-center relative overflow-hidden group h-20 sm:h-24"
                  >
                      <div className="absolute left-0 top-0 bottom-0 w-3 bg-brand-teal"></div>
                      <div className="transform skew-x-12 flex items-center gap-2">
                          <span className="text-2xl sm:text-3xl font-black italic text-white tracking-tighter uppercase drop-shadow-md">{theme.labels.primaryAction}</span>
                          {theme.labels.primaryAction !== 'ADD DRINK' && (
                              <span className="text-[10px] sm:text-xs font-black italic text-white tracking-widest uppercase mt-1 drop-shadow-sm">(ADD DRINK)</span>
                          )}
                      </div>
                  </button>

                  {/* RIGHT: PIT STOP & END */}
                  <div className="w-full sm:flex-1 flex flex-row sm:flex-col gap-3 h-20 sm:h-24">
                      
                      {/* PIT STOP */}
                      <button 
                          onClick={() => {
                              setStats(s => ({...s, waters: (s.waters || 0) + 1, hydration: Math.min(100, s.hydration + 25)}));
                              setNotification({ type: 'water', message: "Hydration +25% 💧" });
                          }} 
                          className="flex-1 bg-blue-600 border-[3px] border-black transform -skew-x-12 shadow-[6px_6px_0px_#000] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all flex items-center justify-center relative overflow-hidden"
                      >
                          <div className="absolute right-0 top-0 bottom-0 w-2 bg-brand-teal"></div>
                          <div className="transform skew-x-12 flex items-center gap-1.5">
                              <span className="text-lg sm:text-xl font-black italic text-white tracking-tighter uppercase drop-shadow-md">{theme.labels.secondaryAction}</span>
                              <span className="text-[8px] sm:text-[10px] font-black italic text-white tracking-widest uppercase mt-1 drop-shadow-sm">(WATER)</span>
                          </div>
                      </button>

                      {/* CHECKERED FLAG */}
                      <button
                          onClick={() => { finalizeSession(); setView('summary'); }}
                          className="flex-1 bg-black border-[3px] border-brand-teal transform -skew-x-12 shadow-[6px_6px_0px_#000] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all flex items-center justify-center relative overflow-hidden group"
                      >
                          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fff 25%, transparent 25%, transparent 75%, #fff 75%, #fff), repeating-linear-gradient(45deg, #fff 25%, transparent 25%, transparent 75%, #fff 75%, #fff)', backgroundPosition: '0 0, 15px 15px', backgroundSize: '30px 30px' }}></div>
                          <div className="transform skew-x-12 flex items-center gap-2 relative z-10 bg-black/80 px-4 py-1.5 border border-zinc-800">
                              <div className="flex gap-1.5 items-center">
                                  <span className="text-sm font-black italic text-brand-teal tracking-tighter uppercase drop-shadow-md">{theme.labels.endSession}</span>
                                  <span className="text-[8px] font-black italic text-white tracking-widest uppercase mt-0.5"></span>
                              </div>
                          </div>
                      </button>
                  </div>
              </div>
          </div>
        </>
      )}

      {/* MODALS */}
      {showLocationPrimer && <LocationPrimer onContinue={confirmLocationPrimer} />}
      {showRapSheet && morning.rapSheet && <RapSheet sheet={morning.rapSheet} onClose={() => setShowRapSheet(false)} />}
      {showLogWorkout && (
        <LogWorkoutModal
          drinks={morning.lastSession?.drinks}
          onClose={() => setShowLogWorkout(false)}
          onSubmit={(input) => { setShowLogWorkout(false); if (morning.logManual(input)) setShowRapSheet(true); }}
        />
      )}
      {showOverlayCreator && <OverlayCreator onClose={() => setShowOverlayCreator(false)} />}
      
      {showSquadManager && (
        <SquadManager 
            db={db} appId={APP_ID} user={user} currentSquadId={squadId}
            username={username || (email ? email.split('@')[0] : 'Anonymous')} 
            onClose={() => setShowSquadManager(false)} hasUnread={hasUnreadMessages} 
            onOpenChat={() => { setShowSquadManager(false); setShowChat(true); setHasUnreadMessages(false); }}
        />
      )}

      {showChat && <SquadChat db={db} appId={APP_ID} squadCode={squadId} user={user} username={username || (email ? email.split('@')[0] : 'Unknown')} onClose={() => setShowChat(false)} />}
      {showLeaderboard && <Leaderboard db={db} appId={APP_ID} currentUser={user} onClose={() => setShowLeaderboard(false)} />}
      {showDrinkSelector && <DrinkSelector currentDrink={stats.drinkOfChoice} onSelect={(d) => setStats(s => ({...s, drinkOfChoice: d}))} onClose={() => setShowDrinkSelector(false)} />}
    </div>
  );
};

export default App;
